/**
 * Réglages IA de la galerie : visibles avec un add-on IA effectif et ia:read.
 * Les écritures exigent ia:settings ; la voix est versionnée. Les montants en
 * cents sont affichés en dollars US ; l’aperçu reste du texte jamais interprété.
 */
import { useState, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ADDONS_IA, USAGES_IA, ETATS_APPEL_IA, formatDate, type VoixIa } from '@artosera/shared';
import { Button, Checkbox, Input, Textarea } from '@artosera/ui';
import {
  useAppelsIa,
  useEtatIa,
  useVoixIa,
  useEnregistrerVoix,
  useApercuIa,
  type Voix,
  type EtatIa,
  type FiltresIa,
} from '../../api/ia.js';
import { estApiError } from '../../api/errors.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import styles from './IaPage.module.css';

function moisEnLettres(mois: string, langue: string): string {
  return formatDate(new Date(`${mois}-01T12:00:00Z`), langue === 'en' ? 'en' : 'fr', 'mois');
}

export function IaPage(): JSX.Element {
  const { galerie, galerieSlug, compte } = useShellContext();
  if (!galerieALaction(galerie, 'ia:read') || !ADDONS_IA.some((a) => galerieAModule(galerie, a)))
    return <NotFoundPage />;
  return (
    <ContenuIa
      key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}:${galerie.modules?.join(',')}`}
    />
  );
}

function ContenuIa(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const [notice, setNotice] = useState<{ texte: string; erreur: boolean } | null>(null);
  const etat = useEtatIa(galerieSlug);
  const voix = useVoixIa(galerieSlug);
  // Les réglages d'IA servent plusieurs modules : ils ne s'écrivent que si l'un
  // d'eux au moins est ouvert en écriture, pas seulement consultable.
  const modifiable =
    galerieALaction(galerie, 'ia:settings') &&
    ADDONS_IA.some((addon) => galerieEcritModule(galerie, addon));
  const dollars = (cents: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.titre}>{t('ia.titre')}</h1>
        <p>{t('ia.description')}</p>
      </header>
      <section className={styles.section} aria-labelledby="ia-etat">
        <h2 id="ia-etat">{t('ia.etat')}</h2>
        {etat.isPending && <p>{t('ia.chargement')}</p>}
        {etat.isError && <p role="alert">{t('ia.erreur')}</p>}
        {etat.data && (
          <>
            <p>{t(`ia.disponibilites.${etat.data.disponibilite}`)}</p>
            <h3>
              {t('ia.consommation', {
                mois: moisEnLettres(etat.data.consommation.mois, i18n.language),
              })}
            </h3>
            <p>
              {t('ia.montants', {
                depense: dollars(etat.data.consommation.depenseCents),
                plafond: dollars(etat.data.consommation.plafondCents),
                part: new Intl.NumberFormat(i18n.language, {
                  style: 'percent',
                  maximumFractionDigits: 1,
                }).format(etat.data.consommation.partUtilisee),
              })}
            </p>
            <progress
              aria-label={t('ia.consommation', {
                mois: moisEnLettres(etat.data.consommation.mois, i18n.language),
              })}
              max={1}
              value={Math.min(1, etat.data.consommation.partUtilisee)}
            />
            {etat.data.consommation.seuil80Atteint && (
              <p role="status" className={styles.seuil}>
                {t('ia.seuil')}
              </p>
            )}
            <p>{t('ia.plafondAide')}</p>
          </>
        )}
      </section>
      <section className={styles.section} aria-labelledby="ia-voix">
        <h2 id="ia-voix">{t('ia.voix')}</h2>
        <p>{t('ia.confidentialite')}</p>
        {!modifiable && <p>{t('ia.lectureSeule')}</p>}
        {voix.isPending && <p>{t('ia.chargement')}</p>}
        {voix.isError && <p role="alert">{t('ia.erreur')}</p>}
        {notice && <p role={notice.erreur ? 'alert' : 'status'}>{notice.texte}</p>}
        {voix.data && (
          <VoixForm
            notifier={setNotice}
            key={voix.data.version}
            voix={voix.data}
            modifiable={modifiable}
            recharger={async () => {
              const r = await voix.refetch();
              if (r.isError) throw r.error;
            }}
          />
        )}
      </section>
      <Apercu disponibilite={etat.data?.disponibilite} modifiable={modifiable} />
      <Journal dollars={dollars} />
    </div>
  );
}

function Texte({
  id,
  label,
  value,
  max,
  onChange,
  readOnly = false,
}: {
  id: string;
  label: string;
  value: string;
  max: number;
  onChange: (v: string) => void;
  readOnly?: boolean;
}): JSX.Element {
  return (
    <Field label={label} htmlFor={id}>
      <Textarea
        id={id}
        value={value}
        maxLength={max}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={`${id}-borne`}
      />
      <small id={`${id}-borne`}>
        {value.length} / {max}
      </small>
    </Field>
  );
}

function VoixForm({
  voix,
  modifiable,
  recharger,
  notifier,
}: {
  notifier: (v: { texte: string; erreur: boolean } | null) => void;
  voix: Voix;
  modifiable: boolean;
  recharger: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [form, setForm] = useState<VoixIa>(() => ({
    ton: voix.ton,
    adresse: voix.adresse,
    langues: voix.langues,
    signature: voix.signature,
    consignes: voix.consignes,
    sujetsExclus: voix.sujetsExclus,
    exemples: voix.exemples,
  }));
  const enregistrer = useEnregistrerVoix(galerieSlug);
  const changer = <K extends keyof VoixIa>(cle: K, valeur: VoixIa[K]) =>
    setForm((f) => ({ ...f, [cle]: valeur }));
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        if (!modifiable || enregistrer.isPending) return;
        notifier(null);
        void enregistrer
          .mutateAsync({ ...form, version: voix.version })
          .then(() => notifier({ texte: t('ia.enregistre'), erreur: false }))
          .catch(async (err: unknown) => {
            if (estPorteeObsolete(err)) return;
            if (estApiError(err) && err.status === 409) {
              try {
                await recharger();
                notifier({ texte: t('ia.conflit'), erreur: true });
              } catch {
                notifier({ texte: t('ia.conflitErreur'), erreur: true });
              }
            } else notifier({ texte: t('ia.erreur'), erreur: true });
          });
      }}
    >
      <fieldset disabled={!modifiable || enregistrer.isPending} className={styles.formulaire}>
        <legend>{t('ia.style')}</legend>
        <Choix
          id="ia-ton"
          label={t('ia.ton')}
          value={form.ton}
          onChange={(v) => changer('ton', v as VoixIa['ton'])}
        >
          {['sobre', 'chaleureux', 'soutenu'].map((v) => (
            <option key={v} value={v}>
              {t(`ia.tons.${v}`)}
            </option>
          ))}
        </Choix>
        <Choix
          id="ia-adresse"
          label={t('ia.adresse')}
          value={form.adresse}
          onChange={(v) => changer('adresse', v as VoixIa['adresse'])}
        >
          {['vous', 'tu'].map((v) => (
            <option key={v} value={v}>
              {t(`ia.adresses.${v}`)}
            </option>
          ))}
        </Choix>
        <fieldset>
          <legend>{t('ia.langues')}</legend>
          {(['fr', 'en'] as const).map((l) => (
            <Checkbox
              key={l}
              id={`ia-langue-${l}`}
              label={t(`ia.languesNoms.${l}`)}
              checked={form.langues.includes(l)}
              disabled={!modifiable || (form.langues.length === 1 && form.langues.includes(l))}
              onCheckedChange={(c) =>
                changer('langues', c ? [...form.langues, l] : form.langues.filter((v) => v !== l))
              }
            />
          ))}
        </fieldset>
        {(['signature', 'consignes'] as const).map((c) => (
          <Texte
            key={c}
            id={`ia-${c}`}
            label={t(`ia.${c}`)}
            value={form[c]}
            max={c === 'signature' ? 500 : 2000}
            readOnly={!modifiable}
            onChange={(v) => changer(c, v)}
          />
        ))}
        <fieldset className={styles.formulaire}>
          <legend>
            {t('ia.sujets')} ({form.sujetsExclus.length} / 20)
          </legend>
          {form.sujetsExclus.map((s, index) => (
            <div className={styles.ligne} key={index}>
              <Texte
                id={`ia-sujet-${index}`}
                label={t('ia.sujet', { n: index + 1 })}
                value={s}
                max={80}
                readOnly={!modifiable}
                onChange={(v) =>
                  changer(
                    'sujetsExclus',
                    form.sujetsExclus.map((x, i) => (i === index ? v : x)),
                  )
                }
              />
              {modifiable && (
                <Button
                  type="button"
                  variant="secondary"
                  aria-label={t('ia.retirerSujet', { n: index + 1 })}
                  onClick={() =>
                    changer(
                      'sujetsExclus',
                      form.sujetsExclus.filter((_, i) => i !== index),
                    )
                  }
                >
                  {t('ia.retirer')}
                </Button>
              )}
            </div>
          ))}
          {modifiable && (
            <Button
              type="button"
              variant="secondary"
              disabled={form.sujetsExclus.length >= 20}
              onClick={() => changer('sujetsExclus', [...form.sujetsExclus, ''])}
            >
              {t('ia.ajouterSujet')}
            </Button>
          )}
        </fieldset>
        <fieldset className={styles.formulaire}>
          <legend>
            {t('ia.exemples')} ({form.exemples.length} / 5)
          </legend>
          {form.exemples.map((ex, index) => (
            <div className={styles.exemple} key={index}>
              {(['titre', 'texte'] as const).map((c) => (
                <Texte
                  key={c}
                  id={`ia-exemple-${index}-${c}`}
                  label={t(`ia.exemple${c === 'titre' ? 'Titre' : 'Texte'}`, { n: index + 1 })}
                  value={ex[c]}
                  max={c === 'titre' ? 120 : 4000}
                  readOnly={!modifiable}
                  onChange={(v) =>
                    changer(
                      'exemples',
                      form.exemples.map((x, i) => (i === index ? { ...x, [c]: v } : x)),
                    )
                  }
                />
              ))}
              {modifiable && (
                <Button
                  type="button"
                  variant="secondary"
                  aria-label={t('ia.retirerExemple', { n: index + 1 })}
                  onClick={() =>
                    changer(
                      'exemples',
                      form.exemples.filter((_, i) => i !== index),
                    )
                  }
                >
                  {t('ia.retirer')}
                </Button>
              )}
            </div>
          ))}
          {modifiable && (
            <Button
              type="button"
              variant="secondary"
              disabled={form.exemples.length >= 5}
              onClick={() => changer('exemples', [...form.exemples, { titre: '', texte: '' }])}
            >
              {t('ia.ajouterExemple')}
            </Button>
          )}
        </fieldset>
        {modifiable && <Button type="submit">{t('ia.enregistrer')}</Button>}
      </fieldset>
    </form>
  );
}

function Choix({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <Field label={label} htmlFor={id}>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
    </Field>
  );
}

function Apercu({
  disponibilite,
  modifiable,
}: {
  disponibilite: EtatIa['disponibilite'] | undefined;
  modifiable: boolean;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [question, setQuestion] = useState('');
  const mutation = useApercuIa(galerieSlug);
  let raison = 'generique';
  if (estApiError(mutation.error)) {
    const details = mutation.error.details;
    if (
      details &&
      typeof details === 'object' &&
      'raison' in details &&
      typeof details.raison === 'string'
    )
      raison = details.raison;
    else if (mutation.error.status === 429) raison = 'ia_debit';
    else if (mutation.error.status === 400) raison = 'ia_entree_invalide';
  }
  const raisons = [
    'ia_coupee',
    'ia_plafond',
    'ia_debit',
    'ia_indisponible',
    'ia_refus',
    'ia_invalide',
    'ia_entree_invalide',
  ];
  return (
    <section className={styles.section} aria-labelledby="ia-apercu">
      <h2 id="ia-apercu">{t('ia.apercu')}</h2>
      <p>{t('ia.apercuAide')}</p>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          if (modifiable && disponibilite === 'active' && question.trim() && !mutation.isPending)
            mutation.mutate(question);
        }}
      >
        <Texte
          id="ia-question"
          label={t('ia.question')}
          value={question}
          max={1000}
          readOnly={!modifiable}
          onChange={setQuestion}
        />
        <Button
          type="submit"
          disabled={
            !modifiable || disponibilite !== 'active' || !question.trim() || mutation.isPending
          }
        >
          {t('ia.essayer')}
        </Button>
      </form>
      {mutation.isPending && <p role="status">{t('ia.essaiEnCours')}</p>}
      {mutation.isError && !estPorteeObsolete(mutation.error) && (
        <p role="alert">{t(`ia.erreurs.${raisons.includes(raison) ? raison : 'generique'}`)}</p>
      )}
      {mutation.data && (
        <p className={styles.reponse} role="status">
          {mutation.data.texte}
        </p>
      )}
    </section>
  );
}

function Journal({ dollars }: { dollars: (c: number) => string }): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [filtres, setFiltres] = useState<FiltresIa>({ page: 1, usage: '', etat: '', mois: '' });
  const journal = useAppelsIa(galerieSlug, filtres);
  const filtrer = (cle: string, valeur: string) =>
    setFiltres((f) => ({ ...f, [cle]: valeur, page: 1 }));
  const date = (v: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Europe/Paris',
    }).format(new Date(v));
  const cellules = (a: NonNullable<typeof journal.data>['appels'][number]) => [
    date(a.creeLe),
    t(`ia.usages.${a.usage}`, { nsSeparator: false }),
    t(`ia.etats.${a.etat}`),
    dollars(a.coutCents),
    t('ia.dureeValeur', { n: a.dureeMs }),
  ];
  const colonnes = ['date', 'usage', 'etat', 'cout', 'duree'];
  return (
    <section className={styles.section} aria-labelledby="ia-journal">
      <h2 id="ia-journal">{t('ia.journal')}</h2>
      <p>{t('ia.journalAide')}</p>
      <div className={styles.filtres}>
        <Choix
          id="ia-filtre-usage"
          label={t('ia.usage')}
          value={filtres.usage}
          onChange={(v) => filtrer('usage', v)}
        >
          <option value="">{t('ia.tousUsages')}</option>
          {Object.keys(USAGES_IA).map((u) => (
            <option key={u} value={u}>
              {t(`ia.usages.${u}`, { nsSeparator: false })}
            </option>
          ))}
        </Choix>
        <Choix
          id="ia-filtre-etat"
          label={t('ia.etat')}
          value={filtres.etat}
          onChange={(v) => filtrer('etat', v)}
        >
          <option value="">{t('ia.tousEtats')}</option>
          {ETATS_APPEL_IA.map((s) => (
            <option key={s} value={s}>
              {t(`ia.etats.${s}`)}
            </option>
          ))}
        </Choix>
        <Field label={t('ia.mois')} htmlFor="ia-filtre-mois">
          <Input
            id="ia-filtre-mois"
            type="month"
            aria-describedby={filtres.mois ? 'ia-filtre-mois-libelle' : undefined}
            value={filtres.mois}
            onChange={(e) => filtrer('mois', e.target.value)}
          />
          {filtres.mois && (
            <small id="ia-filtre-mois-libelle">{moisEnLettres(filtres.mois, i18n.language)}</small>
          )}
        </Field>
      </div>
      {journal.isPending && <p>{t('ia.chargement')}</p>}
      {journal.isError && <p role="alert">{t('ia.erreur')}</p>}
      {journal.data && (
        <>
          {journal.data.appels.length === 0 ? (
            <p>{t('ia.journalVide')}</p>
          ) : (
            <>
              <table className={styles.table}>
                <caption>{t('ia.journal')}</caption>
                <thead>
                  <tr>
                    {colonnes.map((c) => (
                      <th scope="col" key={c}>
                        {t(`ia.${c}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {journal.data.appels.map((a) => (
                    <tr key={a.id}>
                      {cellules(a).map((v, i) => (
                        <td key={i}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className={styles.mobile} aria-label={t('ia.journal')}>
                {journal.data.appels.map((a) => (
                  <li key={a.id}>
                    <dl>
                      {cellules(a).map((v, i) => (
                        <div key={i}>
                          <dt>{t(`ia.${colonnes[i]}`)}</dt>
                          <dd>{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </li>
                ))}
              </ul>
            </>
          )}
          <nav className={styles.pagination} aria-label={t('ia.pagination')}>
            <Button
              variant="secondary"
              disabled={filtres.page <= 1}
              onClick={() => setFiltres((f) => ({ ...f, page: f.page - 1 }))}
            >
              {t('ia.precedent')}
            </Button>
            <span>
              {t('ia.page', {
                n: journal.data.page,
                total: Math.max(1, Math.ceil(journal.data.total / 50)),
              })}
            </span>
            <Button
              variant="secondary"
              disabled={filtres.page * 50 >= journal.data.total}
              onClick={() => setFiltres((f) => ({ ...f, page: f.page + 1 }))}
            >
              {t('ia.suivant')}
            </Button>
          </nav>
        </>
      )}
    </section>
  );
}
